import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isAxiosError } from "axios";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { GET_PROJECT_DIMENSIONS_KEY } from "@/zapi-frontend/shared/services/useProjectDimensions";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { GET_SAVED_FILTERS_KEY } from "@/zapi-frontend/shared/services/useSavedFilters";
import { useCreateProjectDimension } from "../services/useCreateProjectDimension";
import { useUpdateProjectDimension } from "../services/useUpdateProjectDimension";
import { useDeleteProjectDimension } from "../services/useDeleteProjectDimension";
import { useReorderProjectDimensions } from "../services/useReorderProjectDimensions";
import type { TDimensionFormState } from "./dimensionForm";
import { useTranslation } from "react-i18next";

type TDestructive = {
  message: string;
  affectedCount: number;
  pending: () => Promise<void>;
};

type TParams = {
  orgId: number;
  dimensions: TOrganizationProjectDimension[];
  onAfterCreate: () => void;
  onAfterUpdate: () => void;
};

export function useManageDimensions({ orgId, dimensions, onAfterCreate, onAfterUpdate }: TParams) {
  const { t } = useTranslation("projects");
  const qc = useQueryClient();
  const [orderedDims, setOrderedDims] = useState<TOrganizationProjectDimension[]>(dimensions);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(null);
  const [destructive, setDestructive] = useState<TDestructive | null>(null);

  useEffect(() => {
    setOrderedDims(dimensions);
  }, [dimensions]);

  const invalidateProjectsList = () => {
    qc.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY, orgId] });
  };

  const createMut = useCreateProjectDimension({
    onSuccess: (created) => {
      flushSync(() => {
        setOrderedDims((prev) => [...prev, created]);
      });
      qc.setQueryData<TOrganizationProjectDimension[]>([GET_PROJECT_DIMENSIONS_KEY, orgId], (old) => [
        ...(old ?? []),
        created
      ]);
      invalidateProjectsList();
      toast.success(t("dimensions.toasts.created"));
      onAfterCreate();
    },
    onError: (e) => {
      toast.error(e.response?.data?.message || t("dimensions.errors.createFailed"));
    }
  });

  const updateMut = useUpdateProjectDimension({
    onSuccess: (updated) => {
      flushSync(() => {
        setOrderedDims((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      });
      qc.setQueryData<TOrganizationProjectDimension[]>([GET_PROJECT_DIMENSIONS_KEY, orgId], (old) =>
        (old ?? []).map((d) => (d.id === updated.id ? updated : d))
      );
      invalidateProjectsList();
      toast.success(t("dimensions.toasts.updated"));
      setDestructive(null);
      onAfterUpdate();
    },
    onError: (e, vars) => {
      if (isAxiosError(e) && e.response?.status === 409) {
        const data = e.response.data as { message?: string; affectedCount?: number };
        if (data?.affectedCount != null && data?.message) {
          setDestructive({
            message: data.message,
            affectedCount: data.affectedCount,
            pending: async () => {
              await updateMut.mutateAsync({ ...vars, confirmDestructive: true });
            }
          });
          return;
        }
      }
      toast.error(e.response?.data?.message || t("dimensions.errors.updateFailed"));
    }
  });

  const deleteMut = useDeleteProjectDimension({
    onSuccess: (id) => {
      qc.setQueryData<TOrganizationProjectDimension[]>([GET_PROJECT_DIMENSIONS_KEY, orgId], (old) =>
        (old ?? []).filter((d) => d.id !== id)
      );
      setConfirmingDeleteId(null);
      toast.success(t("dimensions.toasts.removed"));
      invalidateProjectsList();
      qc.invalidateQueries({ queryKey: [GET_SAVED_FILTERS_KEY, orgId] });
    },
    onError: (e) => {
      toast.error(e.response?.data?.message || t("dimensions.errors.deleteFailed"));
      setConfirmingDeleteId(null);
    }
  });

  const reorderMut = useReorderProjectDimensions({
    onError: (e) => {
      qc.invalidateQueries({ queryKey: [GET_PROJECT_DIMENSIONS_KEY, orgId] });
      toast.error(e.response?.data?.message || t("dimensions.errors.reorderFailed"));
    }
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedDims.findIndex((d) => d.id === active.id);
    const newIndex = orderedDims.findIndex((d) => d.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(orderedDims, oldIndex, newIndex);
    flushSync(() => {
      setOrderedDims(next);
    });
    qc.setQueryData<TOrganizationProjectDimension[]>([GET_PROJECT_DIMENSIONS_KEY, orgId], next);
    reorderMut.mutate({ orgId, orderedIds: next.map((d) => d.id) });
  };

  const create = (form: TDimensionFormState) => {
    createMut.mutate({ orgId, name: form.name, type: form.type, options: form.options, color: form.color });
  };

  const update = (dimensionId: number, form: TDimensionFormState) => {
    updateMut.mutate({
      orgId,
      dimensionId,
      name: form.name,
      type: form.type,
      options: form.options,
      color: form.color
    });
  };

  const remove = (id: number) => {
    deleteMut.mutate({ orgId, dimensionId: id });
  };

  const isFormPending = createMut.isPending || updateMut.isPending;
  const isDeletePending = deleteMut.isPending;
  const deletingId = isDeletePending ? confirmingDeleteId : null;

  return {
    orderedDims,
    confirmingDeleteId,
    setConfirmingDeleteId,
    deletingId,
    isFormPending,
    isDeletePending,
    destructive,
    setDestructive,
    handleDragEnd,
    create,
    update,
    remove
  };
}
